// Boss fights. The boss attacks (shockwaves, falling blocks, minions), then gets dizzy:
// a question appears and Sai must smash the pillar with the right answer to zap the boss.
import * as THREE from 'three';
import { atlasBox, getAtlasMaterial, slimeModel, crownModel } from './models.js';
import { Character, PX } from './character.js';
import { paintSkin } from './skins.js';
import { makeLabelTexture } from './textures.js';
import { blockGeometry } from './voxel.js';
import { B } from './blocks.js';
import { audio } from './audio.js';
import { haptics } from './haptics.js';
import { disposeObj } from './entities.js';
import { clamp } from './util.js';

const PILLAR_COLORS = ['#ff4d6d', '#3d8bff', '#ffc21f', '#2fc85a'];
const PILLAR_NAMES = ['RED', 'BLUE', 'YELLOW', 'GREEN'];
const PILLAR_TILES = ['neon_red', 'neon_blue', 'neon_yellow', 'neon_lime'];
const CENTER = new THREE.Vector3(0.5, 41, 0.5);
const _v = new THREE.Vector3();

// A block golem (Adventure bosses and Explore's Mossy Giant). Tiles come from t: {top, body, front?, head, limb, eye, crown?}.
export function golemModel(t) {
  const g = new THREE.Group();
  // own copy of the block material so hit-flashes only light up this golem
  const mat = getAtlasMaterial().clone();
  // clone() copies userData; this copy belongs to the golem, so disposeObj frees it.
  mat.userData.shared = false;
  const box = (w, h, d, tiles) => {
    const m = new THREE.Mesh(atlasBox(w, h, d, tiles), mat);
    m.castShadow = true;
    return m;
  };
  const torso = box(2.2, 2.0, 1.3, { top: t.top, side: t.body, front: t.front || t.body });
  torso.position.y = 2.9;
  const headPivot = new THREE.Group();
  headPivot.position.y = 3.9;
  const head = box(1.3, 1.3, 1.3, { top: t.top, side: t.head });
  head.position.y = 0.65;
  const eyeMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(t.eye).multiplyScalar(2.2) });
  const e1 = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.18, 0.05), eyeMat);
  const e2 = e1.clone();
  e1.position.set(-0.3, 0.75, 0.66);
  e2.position.set(0.3, 0.75, 0.66);
  headPivot.add(head, e1, e2);
  const arm = (side) => {
    const p = new THREE.Group();
    p.position.set(side * 1.45, 3.7, 0);
    const a = box(0.75, 2.5, 0.75, { top: t.top, side: t.limb });
    a.position.y = -1.1;
    p.add(a);
    return p;
  };
  const leg = (side) => {
    const p = new THREE.Group();
    p.position.set(side * 0.55, 1.9, 0);
    const l = box(0.85, 1.9, 0.85, { top: t.top, side: t.limb });
    l.position.y = -0.95;
    p.add(l);
    return p;
  };
  const armL = arm(1), armR = arm(-1), legL = leg(1), legR = leg(-1);
  g.add(torso, headPivot, armL, armR, legL, legR);
  if (t.crown) {
    const c = crownModel();
    c.scale.setScalar(2.4);
    c.position.y = 1.3 - 8 * PX * 2.4;
    headPivot.add(c);
  }
  return { group: g, head: headPivot, armL, armR, legL, legR, eyeMat, height: 5.4 };
}

function buildBoss(kind) {
  if (kind === 'slime') {
    const g = new THREE.Group();
    const s = slimeModel('#6fd65a', '#3f9a32', 3.2);
    g.add(s);
    const c = crownModel();
    const sc = 5;
    c.scale.setScalar(sc);
    c.position.y = 3.2 - 8 * PX * sc;
    g.add(c);
    return { group: g, slime: s, height: 4, radius: 1.8 };
  }
  if (kind === 'glitch') {
    const ch = new Character(paintSkin('glitch', 0, 0), { scale: 2.7 });
    ch.setAccessory('crown');
    ch.setItem('glitch');
    return { group: ch.root, ch, height: 5, radius: 1.4 };
  }
  const tiles = {
    golem: { top: 'sandstone_top', body: 'sandstone_side', front: 'chiseled_sandstone', head: 'chiseled_sandstone', limb: 'sandstone_side', eye: '#ffd040' },
    ice: { top: 'snow', body: 'packed_ice', head: 'ice', limb: 'packed_ice', eye: '#4fc8ff' },
    magma: { top: 'magma', body: 'magma', head: 'magma_rock', limb: 'basalt', eye: '#ffe040', crown: true },
  }[kind];
  const gm = golemModel(tiles);
  gm.group.scale.setScalar(1.25);
  return { ...gm, height: 6.6, radius: 1.7 };
}

class Pillar {
  constructor(boss, i, theme) {
    this.boss = boss;
    this.i = i;
    const L = boss.level;
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    this.pos = new THREE.Vector3(CENTER.x + Math.sin(a) * 9.5, CENTER.y, CENTER.z + Math.cos(a) * 9.5);
    const g = new THREE.Group();
    g.position.copy(this.pos);
    const mat = getAtlasMaterial();
    const base = new THREE.Mesh(atlasBox(1.4, 2.2, 1.4, { top: theme.stoneTile, side: theme.stoneTile }), mat);
    base.position.y = 1.1;
    base.castShadow = true;
    const cap = new THREE.Mesh(atlasBox(1.6, 0.5, 1.6, { top: PILLAR_TILES[i], side: PILLAR_TILES[i] }), mat);
    cap.position.y = 2.45;
    cap.castShadow = true;
    this.glowMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(PILLAR_COLORS[i]).multiplyScalar(1.6), transparent: true, opacity: 0 });
    const glow = new THREE.Mesh(new THREE.BoxGeometry(1.75, 0.6, 1.75), this.glowMat);
    glow.position.y = 2.45;
    this.label = new THREE.Sprite(new THREE.SpriteMaterial({ transparent: true, depthWrite: false }));
    this.label.position.y = 4.1;
    this.label.scale.set(2.2, 2.2, 1);
    this.label.visible = false;
    g.add(base, cap, glow, this.label);
    this.obj = g;
    L.group.add(g);
    this.meshes = [base, cap, this.label];
    this.col = { min: new THREE.Vector3(this.pos.x - 0.7, this.pos.y, this.pos.z - 0.7), max: new THREE.Vector3(this.pos.x + 0.7, this.pos.y + 2.7, this.pos.z + 0.7), solid: true };
    L.colliders.push(this.col);
    this.shakeT = 0;
    this.disabled = false;
  }
  setText(text) {
    if (this.label.material.map) this.label.material.map.dispose();
    if (!text) {
      this.label.visible = false;
      return;
    }
    this.label.material.map = makeLabelTexture(text, { bg: '#1d2236', border: PILLAR_COLORS[this.i], w: 256, h: 256 });
    this.label.material.needsUpdate = true;
    this.label.visible = true;
    this.label.scale.set(0.01, 0.01, 1);
    this.pop = 0;
  }
  update(dt, t, active) {
    if (this.pop !== undefined && this.pop < 1) {
      this.pop = Math.min(1, this.pop + dt * 3);
      const s = 2.3 * (1 - Math.pow(1 - this.pop, 3)) + Math.sin(this.pop * Math.PI) * 0.4;
      this.label.scale.set(s, s, 1);
    }
    this.glowMat.opacity = active && !this.disabled ? 0.35 + Math.sin(t * 5 + this.i) * 0.2 : this.flashGreen > 0 ? 0.8 : 0;
    if (this.flashGreen > 0) {
      this.flashGreen -= dt;
      this.glowMat.color.set('#40ff70').multiplyScalar(1.8);
    } else this.glowMat.color.set(PILLAR_COLORS[this.i]).multiplyScalar(1.6);
    if (this.shakeT > 0) {
      this.shakeT -= dt;
      this.obj.position.x = this.pos.x + (Math.random() - 0.5) * 0.25;
      this.obj.position.z = this.pos.z + (Math.random() - 0.5) * 0.25;
    } else this.obj.position.copy(this.pos);
    if (this.disabled) this.label.material.opacity = 0.35;
    else this.label.material.opacity = 1;
  }
  dispose() {
    this.boss.level.group.remove(this.obj);
    if (this.label.material.map) this.label.material.map.dispose();
    disposeObj(this.obj);
  }
}

export class Boss {
  constructor(level, def, theme) {
    this.level = level;
    this.def = def;
    this.kind = def.kind;
    this.maxHp = def.hp;
    this.hp = def.hp;
    const m = buildBoss(def.kind);
    this.m = m;
    this.obj = new THREE.Group();
    this.obj.add(m.group);
    this.pos = new THREE.Vector3(CENTER.x, CENTER.y, CENTER.z + 3);
    this.obj.position.copy(this.pos);
    this.obj.rotation.y = Math.PI;
    level.group.add(this.obj);
    this.radius = m.radius;
    this.state = 'wait';
    this.t = 0;
    this.stateT = 0;
    this.attacks = 0;
    this.attackCD = 1.5;
    this.shocks = [];
    this.drops = [];
    this.vy = 0;
    this.yOff = 0;
    const stoneTile = { plains: 'cobble', desert: 'chiseled_sandstone', snow: 'packed_ice', lava: 'obsidian', glitch: 'void_brick' }[level.themeKey] || 'cobble';
    this.pillars = [0, 1, 2, 3].map((i) => new Pillar(this, i, { stoneTile }));
    this.q = null;
    this.stars = new THREE.Group();
    for (let i = 0; i < 5; i++) {
      const s = new THREE.Mesh(new THREE.OctahedronGeometry(0.22), new THREE.MeshBasicMaterial({ color: new THREE.Color(2, 1.8, 0.5) }));
      this.stars.add(s);
    }
    this.stars.visible = false;
    level.group.add(this.stars);
    this.mats = [];
    m.group.traverse((o) => o.material && this.mats.push(o.material));
    this.firstHitOnQuestion = true;
    this.hud = level.game.ui.bossHud;
    this.hud.show(def.name, this.hp, this.maxHp);
    this.minionKind = { slime: 'slime', golem: 'zombie', ice: 'flyer', magma: 'boom', glitch: 'flyer' }[def.kind];
  }

  roar() {
    audio.play('bossRoar');
    haptics.play('boss');
    this.level.shake(0.8);
    this.hud.message(`${this.def.name} wants to scramble your brain! Dodge the attacks, then smash the right answer!`);
  }

  setState(s) {
    this.state = s;
    this.stateT = 0;
  }

  update(dt) {
    const L = this.level, pl = L.player;
    this.t += dt;
    this.stateT += dt;
    const toP = _v.set(pl.pos.x - this.pos.x, 0, pl.pos.z - this.pos.z);
    const dist = toP.length();
    // face the player
    if (this.state !== 'dead') {
      const want = Math.atan2(toP.x, toP.z);
      let d = want - this.obj.rotation.y;
      while (d > Math.PI) d -= Math.PI * 2;
      while (d < -Math.PI) d += Math.PI * 2;
      this.obj.rotation.y += d * Math.min(1, dt * (this.state === 'dizzy' ? 0.5 : 3));
    }
    let walking = false;
    switch (this.state) {
      case 'wait':
        if (this.stateT > 2.2 && L.state === 'play') this.setState('attack');
        break;
      case 'attack': {
        // stalk the player
        if (dist > 4.5 && !this.jumping) {
          const sp = 1.7 + (this.maxHp - this.hp) * 0.25;
          const nx = this.pos.x + (toP.x / dist) * sp * dt, nz = this.pos.z + (toP.z / dist) * sp * dt;
          if (Math.hypot(nx - CENTER.x, nz - CENTER.z) < 10.5) {
            this.pos.x = nx;
            this.pos.z = nz;
          }
          walking = true;
        }
        this.attackCD -= dt;
        if (this.attackCD <= 0 && !this.jumping) {
          const roll = Math.random();
          if (roll < 0.4) this.startStomp();
          else if (roll < 0.8 || L.mobs.length >= 2) this.startRain();
          else this.summon();
          this.attacks++;
          this.attackCD = Math.max(1.4, 2.6 - (this.maxHp - this.hp) * 0.2);
        }
        if (this.attacks >= 3 && !this.jumping && this.stateT > 6) this.startDizzy();
        break;
      }
      case 'dizzy':
        this.updateDizzy(dt);
        break;
      case 'hurt':
        if (this.stateT > 1.4) {
          if (this.hp <= 0) this.die();
          else {
            this.attacks = 0;
            this.setState('attack');
            this.attackCD = 1.2;
          }
        }
        break;
      case 'dead':
        this.updateDeath(dt);
        return;
    }
    // stomp jump arc
    if (this.jumping) {
      this.jumpT += dt;
      const T = 0.9;
      const k = this.jumpT / T;
      this.yOff = Math.sin(Math.min(1, k) * Math.PI) * 3.5;
      if (k >= 1) {
        this.jumping = false;
        this.yOff = 0;
        this.land();
      }
    }
    const bob = this.laughT > 0 ? Math.abs(Math.sin(this.laughT * 14)) * 0.3 : 0;
    this.laughT = Math.max(0, (this.laughT || 0) - dt);
    this.obj.position.set(this.pos.x, this.pos.y + this.yOff + bob, this.pos.z);
    this.animate(dt, walking);
    this.updateShocks(dt);
    this.updateDrops(dt);
    for (const p of this.pillars) p.update(dt, this.t, this.state === 'dizzy');
    // contact damage
    if (this.state !== 'dizzy' && this.state !== 'hurt' && dist < this.radius + 0.5 && pl.pos.y < this.pos.y + this.yOff + 4 && pl.pos.y + pl.h > this.pos.y + this.yOff) pl.hurt(1, this.pos, 10);
    // boss acts as a solid-ish wall: push the player out
    if (dist < this.radius + 0.3 && this.state === 'dizzy') {
      pl.pos.x = this.pos.x + (toP.x / (dist || 1)) * (this.radius + 0.3);
      pl.pos.z = this.pos.z + (toP.z / (dist || 1)) * (this.radius + 0.3);
    }
  }

  animate(dt, walking) {
    const m = this.m;
    const t = this.t;
    if (m.slime) {
      const sq = this.jumping ? 1.15 : 1 - Math.max(0, Math.sin(t * 5)) * 0.07;
      m.slime.scale.set(1 / Math.sqrt(sq), sq, 1 / Math.sqrt(sq));
    } else if (m.ch) {
      m.ch.update(dt, { speed: walking ? 3.5 : 0, grounded: !this.jumping, swing: this.slamT >= 0 ? this.slamT : -1, celebrate: this.laughT > 0 });
      if (Math.random() < dt * 2) m.group.position.x = (Math.random() - 0.5) * 0.3;
      else m.group.position.x *= 0.8;
    } else {
      const sw = walking ? Math.sin(t * 5) * 0.5 : 0;
      m.legL.rotation.x = sw;
      m.legR.rotation.x = -sw;
      let arm = walking ? -sw * 0.6 : Math.sin(t * 1.5) * 0.08;
      if (this.jumping) arm = -2.6;
      if (this.state === 'dizzy') arm = 0.3;
      m.armL.rotation.x += (arm - m.armL.rotation.x) * Math.min(1, dt * 8);
      m.armR.rotation.x += ((this.jumping ? -2.6 : -arm) - m.armR.rotation.x) * Math.min(1, dt * 8);
      m.head.rotation.z = this.state === 'dizzy' ? Math.sin(t * 3) * 0.25 : 0;
    }
    if (this.slamT >= 0) {
      this.slamT += dt / 0.6;
      if (this.slamT >= 1) this.slamT = -1;
    }
    // dizzy stars
    this.stars.visible = this.state === 'dizzy';
    if (this.stars.visible) {
      const top = this.pos.y + this.m.height + 0.3;
      this.stars.children.forEach((s, i) => {
        const a = t * 3 + (i / 5) * Math.PI * 2;
        s.position.set(this.pos.x + Math.cos(a) * 1.3, top + Math.sin(t * 4 + i) * 0.15, this.pos.z + Math.sin(a) * 1.3);
        s.rotation.y = t * 4;
      });
    }
    if (this.flash > 0) {
      this.flash -= dt;
      this.setGlow(this.flash > 0 && Math.floor(this.flash * 20) % 2 === 0 ? 1 : 0);
    }
  }

  // hit flash: over-brighten the boss's own materials (never the shared world material)
  setGlow(k) {
    for (const mm of this.mats) {
      if (!mm.color || mm === getAtlasMaterial()) continue;
      if (mm.userData.bc === undefined) mm.userData.bc = mm.color.clone();
      if (k > 0) mm.color.setRGB(2.4, 2.4, 2.4);
      else mm.color.copy(mm.userData.bc);
    }
  }

  // ---------- attacks ----------
  startStomp() {
    this.jumping = true;
    this.jumpT = 0;
    this.slamT = 0;
    this.hud.message('⚠️ SHOCKWAVE! Jump over it!');
  }
  land() {
    const L = this.level;
    audio.play('stomp');
    haptics.play('stomp');
    L.shake(0.9);
    const c = new THREE.Vector3(this.pos.x, this.pos.y, this.pos.z);
    L.fx.rings.add(c, { color: '#ff5a3a', from: 0.5, to: 17, life: 1.7, opacity: 1, bright: 2 });
    L.fx.rings.add(c, { color: '#ffd040', from: 0.3, to: 16.5, life: 1.7, opacity: 0.6, bright: 1.5 });
    L.fx.particles.dust(c, 24, '#cfc0a0');
    this.shocks.push({ c, r: 0.5, speed: 16.5 / 1.7, hit: false });
  }
  updateShocks(dt) {
    const pl = this.level.player;
    for (let i = this.shocks.length - 1; i >= 0; i--) {
      const s = this.shocks[i];
      s.r += s.speed * dt;
      if (s.r > 17) {
        this.shocks.splice(i, 1);
        continue;
      }
      const d = Math.hypot(pl.pos.x - s.c.x, pl.pos.z - s.c.z);
      if (!s.hit && Math.abs(d - s.r) < 0.6 && pl.pos.y < s.c.y + 0.6) {
        s.hit = true;
        pl.hurt(1, s.c, 8);
      }
    }
  }
  startRain() {
    const L = this.level, pl = L.player;
    const n = 3 + (this.maxHp - this.hp);
    this.slamT = 0;
    this.hud.message('⚠️ Blocks incoming! Watch the red circles!');
    for (let i = 0; i < n; i++) {
      let p;
      if (i === 0) p = new THREE.Vector3(pl.pos.x, CENTER.y, pl.pos.z);
      else {
        const a = Math.random() * Math.PI * 2, r = 2 + Math.random() * 11;
        p = new THREE.Vector3(CENTER.x + Math.cos(a) * r, CENTER.y, CENTER.z + Math.sin(a) * r);
      }
      const ring = L.fx.rings.add(p, { color: '#ff2a2a', from: 1.4, to: 1.4, life: 1.5 + i * 0.12, opacity: 0.85, bright: 1.6 });
      void ring;
      const block = { pos: p, t: -i * 0.12, mesh: null, done: false };
      this.drops.push(block);
    }
  }
  updateDrops(dt) {
    const L = this.level, pl = L.player;
    const blockId = { slime: B.BOUNCE, golem: B.SANDSTONE, ice: B.PACKED_ICE, magma: B.MAGMA, glitch: B.GLITCH }[this.kind];
    for (let i = this.drops.length - 1; i >= 0; i--) {
      const d = this.drops[i];
      d.t += dt;
      if (d.t > 1.0 && !d.mesh) {
        d.mesh = new THREE.Mesh(blockGeometry(blockId), L.mats.solid);
        d.mesh.scale.setScalar(1.6);
        d.mesh.castShadow = true;
        d.mesh.position.set(d.pos.x, d.pos.y + 16, d.pos.z);
        L.group.add(d.mesh);
      }
      if (d.mesh && !d.done) {
        const k = Math.min(1, (d.t - 1.0) / 0.4);
        d.mesh.position.y = d.pos.y + 0.8 + 16 * (1 - k * k);
        d.mesh.rotation.y += dt * 5;
        if (k >= 1) {
          d.done = true;
          d.landT = 0;
          audio.play('break');
          L.fx.particles.blockBreak(d.pos.clone().add(new THREE.Vector3(0, 0.8, 0)), blockId);
          L.shake(0.3);
          if (Math.hypot(pl.pos.x - d.pos.x, pl.pos.z - d.pos.z) < 1.5 && pl.pos.y < d.pos.y + 2) pl.hurt(1, d.pos, 8);
        }
      }
      if (d.done) {
        d.landT += dt;
        d.mesh.scale.setScalar(Math.max(0.01, 1.6 * (1 - d.landT * 2)));
        if (d.landT > 0.5) {
          L.group.remove(d.mesh);
          this.drops.splice(i, 1);
        }
      }
    }
  }
  summon() {
    const L = this.level;
    this.hud.message('⚠️ Minions! Bonk them or stomp them!');
    audio.play('bossLaugh');
    for (let i = 0; i < 2; i++) {
      const a = Math.random() * Math.PI * 2;
      const p = new THREE.Vector3(this.pos.x + Math.cos(a) * 3, CENTER.y + (this.minionKind === 'flyer' ? 2.5 : 0.2), this.pos.z + Math.sin(a) * 3);
      L.addMob(this.minionKind, p.x, p.y, p.z);
      L.fx.particles.burst(p, { count: 14, colors: ['#ffffff', '#c080ff'], speed: 3, up: 4, glow: true, size: 0.12 });
    }
  }

  // ---------- question phase ----------
  startDizzy() {
    this.setState('dizzy');
    const L = this.level, g = L.game;
    // clear minions so the kid can focus
    for (const m of L.mobs) m.die(false);
    this.q = g.qe.next({ weights: L.W.topics, tables: L.W.tables, numeric: true, boost: L.wi >= 4 ? 0.3 : 0 });
    this.firstHitOnQuestion = true;
    this.answerIdx = this.q.choices.indexOf(this.q.answer);
    this.pillars.forEach((p, i) => {
      p.disabled = false;
      p.setText(this.q.choices[i]);
    });
    audio.play('bossLaugh');
    this.hud.question(this.q.text, `${this.def.name} is dizzy! Smash the pillar with the right answer! ⚔️`);
    if (g.save.data.settings.readAloud) audio.speak(this.q.speakText || this.q.text.replace(/<[^>]+>/g, ''));
    this.dizzyMax = 26;
  }

  updateDizzy(dt) {
    const L = this.level, input = L.game.input;
    this.hud.timer(1 - this.stateT / this.dizzyMax);
    // keyboard 1-4 or tapping a pillar picks it too
    for (let i = 0; i < 4; i++) if (input.wasPressed(String(i + 1))) this.choose(i);
    for (const tap of input.taps) {
      const ray = new THREE.Raycaster();
      const ndc = new THREE.Vector2((tap.x / innerWidth) * 2 - 1, -(tap.y / innerHeight) * 2 + 1);
      ray.setFromCamera(ndc, L.game.camera);
      for (const p of this.pillars) if (ray.intersectObjects(p.meshes, false).length) this.choose(p.i);
    }
    if (this.stateT > this.dizzyMax && this.state === 'dizzy') {
      this.clearQuestion();
      this.hud.message('Too slow! The boss woke up! 😮');
      this.attacks = 0;
      this.setState('attack');
    }
  }

  trySwordHit(origin, fx, fz) {
    if (this.state !== 'dizzy') return false;
    for (const p of this.pillars) {
      const dx = p.pos.x - origin.x, dz = p.pos.z - origin.z;
      const d = Math.hypot(dx, dz);
      if (d < 2.6 && (d < 1.2 || (dx * fx + dz * fz) / d > 0.1)) {
        this.choose(p.i);
        return true;
      }
    }
    return false;
  }

  choose(i) {
    if (this.state !== 'dizzy') return;
    const p = this.pillars[i];
    if (p.disabled) return;
    const L = this.level, g = L.game;
    const correct = i === this.answerIdx;
    if (this.firstHitOnQuestion) {
      L.stats.asked++;
      g.qe.record(this.q, correct, true);
      g.onAnswer(correct, true);
      if (correct) L.stats.firstTry++;
    }
    if (correct) {
      this.firstHitOnQuestion = false;
      const from = p.pos.clone().add(new THREE.Vector3(0, 2.8, 0));
      const to = this.pos.clone().add(new THREE.Vector3(0, this.m.height * 0.6, 0));
      L.fx.beams.add(from, to, { color: PILLAR_COLORS[i] });
      L.fx.beams.add(from, to, { color: '#ffffff', radius: 0.06 });
      audio.play('zap');
      haptics.play('bossHit');
      setTimeout(() => audio.play('bossHit'), 120);
      this.hp--;
      this.flash = 0.8;
      L.shake(1);
      L.fx.particles.burst(to, { count: 40, colors: ['#ffffff', PILLAR_COLORS[i], '#ffd23f'], speed: 7, up: 5, glow: true, bright: 2, size: 0.15 });
      L.fx.worldUI.float(this.hp <= 0 ? 'FINAL HIT!!' : 'CRITICAL HIT!', to.clone().add(new THREE.Vector3(0, 1.5, 0)), 'ft-gold ft-huge', { life: 1.6 });
      L.addXP(25);
      this.hud.setHp(this.hp);
      this.hud.message(this.hp <= 0 ? `You beat ${this.def.name}!!!` : `${this.q.answer} is right! ${this.hp} more hit${this.hp > 1 ? 's' : ''} to go!`);
      this.clearQuestion();
      this.setState('hurt');
      // knockback
      const away = new THREE.Vector3(this.pos.x - p.pos.x, 0, this.pos.z - p.pos.z).normalize();
      const nx = this.pos.x + away.x * 2, nz = this.pos.z + away.z * 2;
      if (Math.hypot(nx - CENTER.x, nz - CENTER.z) < 10) {
        this.pos.x = nx;
        this.pos.z = nz;
      }
    } else {
      this.firstHitOnQuestion = false;
      p.disabled = true;
      p.shakeT = 0.5;
      audio.play('wrong');
      haptics.play('wrong');
      setTimeout(() => audio.play('bossLaugh'), 250);
      this.laughT = 1.2;
      const right = this.pillars[this.answerIdx];
      right.flashGreen = 3.5;
      const expl = this.q.explain.replace(/<span class="tip">[\s\S]*?<\/span>/g, '').replace(/<br>/g, ' ');
      this.hud.message(`Oops! ${expl} → Smash the <b style="color:${PILLAR_COLORS[this.answerIdx]}">${PILLAR_NAMES[this.answerIdx]}</b> pillar!`, 6);
      this.stateT = Math.min(this.stateT, this.dizzyMax - 12);
    }
  }

  clearQuestion() {
    for (const p of this.pillars) p.setText(null);
    this.hud.question(null);
  }

  // ---------- defeat ----------
  die() {
    this.setState('dead');
    this.deathT = 0;
    this.hud.message(`🎉 ${this.def.name} DEFEATED! 🎉`, 6);
    audio.play('bossRoar');
  }
  updateDeath(dt) {
    const L = this.level;
    this.deathT += dt;
    const P = L.fx.particles;
    this.obj.position.x = this.pos.x + (Math.random() - 0.5) * 0.4;
    this.obj.position.z = this.pos.z + (Math.random() - 0.5) * 0.4;
    if (Math.random() < dt * 10 && this.deathT < 2) {
      const p = this.pos.clone().add(new THREE.Vector3((Math.random() - 0.5) * 3, Math.random() * this.m.height, (Math.random() - 0.5) * 3));
      P.burst(p, { count: 16, colors: ['#ffffff', '#ffd23f', '#ff8a20'], speed: 5, up: 4, glow: true, bright: 2, size: 0.14 });
      audio.play('explosion');
      L.shake(0.4);
    }
    this.setGlow(Math.floor(this.deathT * 12) % 2);
    if (this.deathT > 2.2 && this.obj.visible) {
      this.obj.visible = false;
      const c = this.pos.clone().add(new THREE.Vector3(0, 2, 0));
      P.burst(c, { count: 80, colors: ['#ffffff', '#ffd23f', '#ff8a20', '#7be35a'], speed: 12, up: 8, glow: true, bright: 2.2, size: 0.2, life: 1.4 });
      P.confetti(c, 120, 3);
      L.fx.rings.add(this.pos.clone(), { color: '#ffffff', to: 14, life: 0.9 });
      L.shake(1.2);
      audio.play('explosion');
      audio.play('levelUp');
      haptics.play('levelComplete');
      for (let i = 0; i < 30; i++) L.spawnLoot(c, 'coin');
      for (let i = 0; i < 6; i++) L.spawnLoot(c, 'gem');
      for (let i = 0; i < 5; i++) setTimeout(() => !L.disposed && P.firework(c.clone().add(new THREE.Vector3((Math.random() - 0.5) * 16, 6 + Math.random() * 6, (Math.random() - 0.5) * 16))), 300 + i * 350);
      this.hud.hide();
      setTimeout(() => {
        if (L.disposed) return;
        L.portal.open();
        L.fx.worldUI.float('PORTAL OPEN! ➜', L.portal.center.clone().add(new THREE.Vector3(0, 3, 0)), 'ft-purple ft-big', { life: 2.5 });
      }, 1500);
    }
  }

  dispose() {
    this.level.group.remove(this.obj);
    this.level.group.remove(this.stars);
    if (this.m.ch) this.m.ch.dispose();
    else disposeObj(this.obj);
    for (const p of this.pillars) p.dispose();
    for (const d of this.drops) if (d.mesh) this.level.group.remove(d.mesh);
    this.hud.hide();
  }
}
void clamp;
